import { useMemo } from 'react'
import {
  MessageSquare,
  Mail,
  Pencil,
  Trash2,
  Star,
  RotateCcw,
  Reply,
  AlertTriangle,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import WhatsAppBadge from '@/components/WhatsAppBadge'
import WhatsAppMessageStatus from '@/components/WhatsAppMessageStatus'
import ChatEditor from '@/components/editor/ChatEditor'
import MessageAttachments from '@/components/MessageAttachments'
import MessageBody from '@/components/MessageBody'
import { linkifyText } from '@/lib/linkify'
import QuotedMessage from '@/components/QuotedMessage'
import OnlineIndicator from './OnlineIndicator'
import {
  getInitials,
  getAvatarColor,
  isUserOnline,
  formatRelativeTime,
  formatFullDate,
  formatDateSeparator,
  shouldShowDateSeparator,
  timelineHasMultipleDays,
} from './utils'
import type {
  ConversationDetail,
  Message,
  TimelineItem,
} from './types'

interface MessageTimelineProps {
  timelineItems: TimelineItem[]
  conversation: ConversationDetail
  hasMultipleAgents: boolean
  agentFirstMessageIds: Set<number>
  editingMessageId: number | null
  editingBody: string
  hasEditingText: boolean
  isSavingEdit: boolean
  onEditingBodyChange: (body: string) => void
  onEditingTextChange: (text: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onStartEdit: (message: Message) => void
  canEditMessage: (message: Message) => boolean
  onReply: (message: Message) => void
  onDeleteMessage: (messageId: number) => void
  onRetryMessage: (tempId: string) => void
  onRetryNote: (tempId: string) => void
  lightbox: { open: (images: Array<{ url: string; filename?: string }>, index?: number) => void }
  onVideoClick: (url: string, filename: string) => void
}

export default function MessageTimeline({
  timelineItems,
  conversation,
  hasMultipleAgents,
  agentFirstMessageIds,
  editingMessageId,
  editingBody,
  hasEditingText,
  isSavingEdit,
  onEditingBodyChange,
  onEditingTextChange,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  canEditMessage,
  onReply,
  onDeleteMessage,
  onRetryMessage,
  onRetryNote,
  lightbox,
  onVideoClick,
}: MessageTimelineProps) {
  const timelineDates = useMemo(
    () => timelineItems.map(item => ({ created_at: item.data.created_at })),
    [timelineItems]
  )
  const hasMultipleDays = timelineHasMultipleDays(timelineDates)

  return (
    <>
      {timelineItems.map((item, index) => {
        const currentDate = item.data.created_at
        const prevItem = timelineItems[index - 1]
        const prevDate = prevItem?.data.created_at ?? null
        const showDateSep = shouldShowDateSeparator(currentDate, prevDate, index === 0, hasMultipleDays)

        const dateSeparator = showDateSep ? (
          <div className="flex items-center justify-center my-4">
            <span className="text-xs text-gray-400 bg-white px-3 py-1 rounded-full">
              {formatDateSeparator(currentDate)}
            </span>
          </div>
        ) : null

        if (item.type === 'note') {
          const note = item.data
          const nextItem = timelineItems[index + 1]
          const isSameNoteAgent = (other: TimelineItem | undefined) =>
            other?.type === 'note' && other.data.agent.id === note.agent.id
          const showAvatar = !isSameNoteAgent(prevItem)
          const showTimestamp = !isSameNoteAgent(nextItem)
          const isNoteOptimistic = !!note._optimistic
          const isNoteFailed = !!note._sendFailed
          const noteKey = note._tempId || `note-${note.id}`

          return (
            <div key={noteKey}>
              {dateSeparator}
              <div className="flex gap-2 flex-row-reverse">
                <div className="flex-shrink-0">
                  {showAvatar ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {note.agent.avatar_url ? (
                          <img
                            src={note.agent.avatar_url}
                            alt={note.agent.name}
                            className="w-7 h-7 rounded-full object-cover cursor-default"
                          />
                        ) : (
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium cursor-default ${getAvatarColor(
                              note.agent.name
                            )}`}
                          >
                            {getInitials(note.agent.name)}
                          </div>
                        )}
                      </TooltipTrigger>
                      <TooltipContent side="top">{note.agent.name}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <div className="w-7" />
                  )}
                </div>
                <div className="flex flex-col items-end max-w-[70%]">
                  <div className={`flex items-center gap-1 flex-row-reverse`}>
                    <div className={`px-3 py-2 rounded-2xl rounded-br-md text-sm bg-amber-100 text-amber-900 border border-amber-200${
                      isNoteOptimistic && !isNoteFailed ? ' opacity-60' : ''
                    }${isNoteFailed ? ' ring-1 ring-red-400' : ''}`}>
                      {note.body && <p className="whitespace-pre-wrap">{linkifyText(note.body)}</p>}
                      {note.attachments && note.attachments.length > 0 && (
                        <MessageAttachments
                          attachments={note.attachments}
                          onImageClick={(images, idx) => lightbox.open(images, idx)}
                          onVideoClick={onVideoClick}
                        />
                      )}
                    </div>
                    {isNoteFailed && note._tempId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onRetryNote(note._tempId!)}
                            className="p-1 hover:bg-red-50 rounded text-red-500 hover:text-red-600"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Reenviar nota</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  {isNoteFailed && (
                    <span className="text-xs text-red-500 mt-0.5 px-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Falha ao enviar
                    </span>
                  )}
                  {showTimestamp && !isNoteOptimistic && (
                    <span className="text-xs text-gray-500 mt-0.5 px-1">
                      {formatRelativeTime(note.created_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        }

        // System message
        const message = item.data
        if (message.system_message_type) {
          return (
            <div key={`msg-${message.id}`} id={`msg-${message.id}`}>
              {dateSeparator}
              <div className="flex gap-2 flex-row-reverse">
                <div className="flex-shrink-0">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-blue-500 text-white text-xs font-medium">
                    O
                  </div>
                </div>
                <div className="flex flex-col items-end max-w-[70%]">
                  <div className="px-3 py-2 rounded-2xl text-sm bg-blue-100 text-gray-900 rounded-br-md">
                    {message.system_message_type === 'email_capture' && message.metadata?.email_captured ? (
                      <>
                        <p>Você será notificado aqui e por email</p>
                        <p className="text-gray-500 text-xs mt-1">{message.metadata?.email}</p>
                      </>
                    ) : message.system_message_type === 'rating_request' && message.metadata?.rated ? (
                      <span className="flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-emerald-500" />
                        Avaliação recebida
                      </span>
                    ) : message.system_message_type === 'rating_request' ? (
                      <span className="flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-amber-500" />
                        Avaliação solicitada — aguardando resposta
                      </span>
                    ) : (
                      <span>{message.body}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 mt-0.5 px-1">
                    Operador • {formatRelativeTime(message.created_at)}
                  </span>
                </div>
              </div>
            </div>
          )
        }

        // Regular message
        const isAgent = message.author_type === 'Agent'
        const author = message.author as { id: number; name: string; email?: string | null; avatar_url?: string | null }
        const nextItem = timelineItems[index + 1]
        const showAvatar =
          index === 0 ||
          !prevItem ||
          prevItem.type === 'note' ||
          (prevItem.type === 'message' && (
            prevItem.data.author_type !== message.author_type ||
            prevItem.data.author.id !== message.author.id
          ))
        const showTimestamp =
          !nextItem ||
          nextItem.type === 'note' ||
          (nextItem.type === 'message' && (
            nextItem.data.author_type !== message.author_type ||
            nextItem.data.author.id !== message.author.id
          ))
        const isDeleted = !!message.deleted_at
        const isEditing = editingMessageId === message.id
        const isOptimistic = !!message._optimistic
        const isSendFailed = !!message._sendFailed
        const messageKey = message._tempId || `msg-${message.id}`

        let bubbleStyle = 'bg-gray-100 text-gray-900 rounded-bl-md'
        if (isDeleted) {
          bubbleStyle = 'bg-gray-100 text-gray-400 italic'
        } else if (isAgent) {
          bubbleStyle = 'bg-blue-100 text-gray-900 rounded-br-md'
        }
        const bubbleClass = `px-3 py-2 rounded-2xl text-sm ${bubbleStyle}${
          isOptimistic && !isSendFailed ? ' opacity-60' : ''
        }${isSendFailed ? ' ring-1 ring-red-400' : ''}`

        return (
          <div key={messageKey} id={messageKey}>
            {dateSeparator}
            {agentFirstMessageIds.has(message.id) && (
              <div className="flex items-center justify-center gap-1.5 my-2">
                {isAgent && 'avatar_url' in message.author && message.author.avatar_url ? (
                  <img
                    src={message.author.avatar_url}
                    alt={author.name}
                    className="w-4 h-4 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-medium ${getAvatarColor(author.name)}`}
                  >
                    {author.name.charAt(0)}
                  </div>
                )}
                <span className="text-xs text-gray-400">
                  {author.name.split(' ')[0]} entrou na conversa
                </span>
              </div>
            )}
            <div className={`group flex gap-2 ${isAgent ? 'flex-row-reverse' : ''}`}>
              <div className="flex-shrink-0">
                {showAvatar ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative w-7 h-7">
                        {isAgent && 'avatar_url' in message.author && message.author.avatar_url ? (
                          <img
                            src={message.author.avatar_url}
                            alt={author.name}
                            className="w-7 h-7 rounded-full object-cover cursor-default"
                          />
                        ) : (
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium cursor-default ${getAvatarColor(
                              author.name
                            )}`}
                          >
                            {getInitials(author.name)}
                          </div>
                        )}
                        {!isAgent && isUserOnline(conversation.end_user.last_seen_at ?? null) && (
                          <OnlineIndicator />
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">{author.name}</TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="w-7" />
                )}
              </div>
              <div
                className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'} max-w-[70%]`}
              >
                {showAvatar && isAgent && hasMultipleAgents && (
                  <span className="text-xs font-medium text-gray-500 mb-0.5 px-1">
                    {author.name}
                  </span>
                )}
                <div className={`flex items-center gap-1 ${isAgent ? 'flex-row-reverse' : ''}`}>
                  <div className={bubbleClass}>
                    {isDeleted ? (
                      <span>Esta mensagem foi removida</span>
                    ) : isEditing ? (
                      <div className="min-w-[240px]">
                        <ChatEditor
                          value={editingBody}
                          onChange={onEditingBodyChange}
                          onTextChange={onEditingTextChange}
                          onSubmit={onSaveEdit}
                          placeholder="Editar mensagem..."
                        />
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mt-2">
                            <MessageAttachments
                              attachments={message.attachments}
                              onImageClick={(images, idx) => lightbox.open(images, idx)}
                              onVideoClick={onVideoClick}
                            />
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={onCancelEdit}
                            className="px-2.5 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={onSaveEdit}
                            disabled={isSavingEdit || (!hasEditingText && (message.attachments?.length || 0) === 0)}
                            className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Salvar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {message.replied_to && (
                          <QuotedMessage
                            repliedTo={message.replied_to}
                            onClick={() => {
                              const el = document.getElementById(`msg-${message.replied_to!.id}`)
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                el.classList.add('bg-yellow-50')
                                setTimeout(() => el.classList.remove('bg-yellow-50'), 1500)
                              }
                            }}
                            compact
                          />
                        )}
                        {message.body && !(
                          ['[Áudio]', '[Mensagem de voz]', '[Imagem]', '[Vídeo]', '[Sticker]'].includes(message.body) &&
                          message.attachments?.length > 0
                        ) && <MessageBody content={message.body} onImageClick={lightbox.open} />}
                        {message.attachments && message.attachments.length > 0 && (
                          <MessageAttachments
                            attachments={message.attachments}
                            onImageClick={(images, idx) => lightbox.open(images, idx)}
                            onVideoClick={onVideoClick}
                          />
                        )}
                      </>
                    )}
                  </div>
                  {!isOptimistic && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[11px] text-gray-400 whitespace-nowrap self-center cursor-default">
                          {formatRelativeTime(message.created_at)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {formatFullDate(message.created_at)}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {isSendFailed && message._tempId && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onRetryMessage(message._tempId!)}
                          className="p-1 hover:bg-red-50 rounded text-red-500 hover:text-red-600"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Reenviar mensagem</TooltipContent>
                    </Tooltip>
                  )}
                  {!isDeleted && !isOptimistic && !isEditing && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onReply(message)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-blue-500"
                          >
                            <Reply className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Responder</TooltipContent>
                      </Tooltip>
                      {canEditMessage(message) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => onStartEdit(message)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-blue-500"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Editar mensagem</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onDeleteMessage(message.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Excluir mensagem</TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
                {isSendFailed && (
                  <span className="text-xs text-red-500 mt-0.5 px-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Falha ao enviar
                  </span>
                )}
                {showTimestamp && !isOptimistic && (
                  <span className="text-xs text-gray-500 mt-0.5 px-1 flex items-center gap-1">
                    {!isAgent && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {message.channel === 'email' ? (
                            <Mail className="w-3 h-3" />
                          ) : message.channel === 'whatsapp' ? (
                            <span><WhatsAppBadge size="sm" className="text-green-600" /></span>
                          ) : (
                            <MessageSquare className="w-3 h-3" />
                          )}
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {message.channel === 'email' ? (
                            'Via e-mail'
                          ) : message.channel === 'whatsapp' ? (
                            'Via WhatsApp'
                          ) : (() => {
                            const browser = message.sender_browser?.toLowerCase().includes('unknown') ? null : message.sender_browser
                            const os = message.sender_os
                            const hasUrl = !!message.sender_page_url
                            const hasDeviceInfo = !!(browser || os)

                            if (!hasUrl && !hasDeviceInfo) return 'Via chat'

                            return (
                              <div className="text-left">
                                {hasUrl && (
                                  <>
                                    <div className="font-medium mb-1">URL de origem</div>
                                    <a
                                      href={message.sender_page_url!}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-400 hover:text-blue-300 break-all"
                                    >
                                      {message.sender_page_url}
                                    </a>
                                  </>
                                )}
                                {hasDeviceInfo && (
                                  <div className={hasUrl ? "mt-1 text-gray-300" : ""}>
                                    {[browser, os].filter(Boolean).join(', ')}
                                  </div>
                                )}
                              </div>
                            )
                          })()}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {isAgent && message.channel === 'whatsapp' && message.whatsapp_status && (
                      <WhatsAppMessageStatus
                        status={message.whatsapp_status}
                        errorMessage={message.whatsapp_error_message}
                      />
                    )}
                    {isAgent && message.read_at && !isDeleted && (
                      <>
                        <span>Lida</span>
                        <span>•</span>
                      </>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {formatRelativeTime(message.created_at)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {formatFullDate(message.created_at)}
                      </TooltipContent>
                    </Tooltip>
                    {message.edited_at && !isDeleted && (
                      <>
                        <span>•</span>
                        <span>Editado</span>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
